import { get } from 'svelte/store';

import { devices_config_wave } from 'src/devices_config_wave';
import {
	get_z_wave_channel_structure_from_category,
	get_z_wave_component_from_category,
	getZWaveChannelIndex,
	is_gen_z,
	real_id,
} from 'src/helpers/interface_helpers';
import { zwaveActuatorSetConfig } from 'src/helpers/z_wave_actuator_config_helpers';
import { zwaveCoverSetConfig } from 'src/helpers/z_wave_cover_helpers';
import { postReq } from 'src/services/http';
import { gen2Request } from 'src/services/gen_2_http_reqs';
import { getXzComponentId, xzRequest } from 'src/services/xz_http_reqs';
import { devices } from 'src/stores';
import { devices_settings, devices_status } from 'src/stores';
import type { Device } from 'src/types/Device';
import type { Request } from 'src/types/requests/request';

export type GatewayZWaveNode = {
	component_id: number;
	node_id: number;
	connected: boolean;
	fw_ver: string;
	has_vcomp: boolean;
};

type ResolvedGatewayComponentId = {
	id: number;
	/** True when id comes from per-channel gateway component (`switch:201`), not zwavedevice. */
	fromChannelComponent: boolean;
};

/** Wave gateway device types that host `zwavedevice:*` components. */
export const ZWAVE_GATEWAY_TYPES = ['SNGW-ZW01', 'S3GW-1DZW0EU'] as const;

export const isZWaveGatewayDevice = (device: Device | null | undefined): boolean =>
	Boolean(device?.type && (ZWAVE_GATEWAY_TYPES as readonly string[]).includes(device.type));

/**
 * Finds the Wave gateway that owns this end device (`status.device._gw` / node map / room).
 * When the end device is offline, status may lack `_gw` — fall back to mesh node map, then room.
 */
export const findZWaveGatewayForDevice = (
	device: Device,
	status?: Record<string, any> | null,
): Device | undefined => {
	const devicesMap = get(devices);
	const gateways = Object.values(devicesMap).filter(isZWaveGatewayDevice);
	if (gateways.length === 0) return undefined;
	if (gateways.length === 1) return gateways[0];

	const endStatus =
		status ?? (devices_status.getByRealId(device.id) as Record<string, any> | undefined);
	const settings = get(devices_settings)?.[real_id(device.id)];
	const gatewayMac = endStatus?.device?._gw ?? settings?.device?._gw ?? settings?.sys?.device?._gw;

	if (gatewayMac) {
		const mac = String(gatewayMac);
		const byMac = gateways.find((gateway) => {
			const gatewayId = real_id(gateway.id);
			return gatewayId.includes(mac) || mac.includes(gatewayId);
		});
		if (byMac) return byMac;
	}

	const nodeId = parseZWaveNodeIdFromDeviceId(device?.id);
	if (nodeId !== undefined) {
		const byNode = gateways.filter((gateway) => {
			const gatewayStatus = devices_status.getByRealId(gateway.id) as
				| Record<string, any>
				| undefined;
			return getGatewayZWaveNodes(gatewayStatus).some((node) => node.node_id === nodeId);
		});
		const onlineByNode = byNode.find((gateway) => devices_status.getByRealId(gateway.id)?.online);
		if (onlineByNode) return onlineByNode;
		if (byNode.length === 1) return byNode[0];
	}

	const roomGateways = gateways.filter((gateway) => gateway.room_id === device.room_id);
	const onlineInRoom = roomGateways.find((gateway) => devices_status.getByRealId(gateway.id)?.online);
	if (onlineInRoom) return onlineInRoom;
	if (roomGateways.length === 1) return roomGateways[0];

	return undefined;
};

/**
 * Extracts the Z-Wave node id embedded in a cloud device id (`XZ…N16` → 16).
 * Channel suffixes (`_1`) are ignored.
 */
export const parseZWaveNodeIdFromDeviceId = (deviceId: string | undefined): number | undefined => {
	if (!deviceId) return undefined;
	const match = real_id(deviceId).match(/N(\d+)$/i);
	if (!match) return undefined;
	const nodeId = Number(match[1]);
	return Number.isFinite(nodeId) ? nodeId : undefined;
};

/**
 * Collects gateway `zwavedevice:*` entries and marks whether each has a matching `vcomps` entry.
 */
export const getGatewayZWaveNodes = (status: Record<string, any> | undefined): GatewayZWaveNode[] => {
	if (!status) return [];

	const vcomps: string[] = Array.isArray(status.vcomps) ? status.vcomps : [];
	const vcompIds = new Set(
		vcomps
			.map((entry) => {
				const match = String(entry).match(/:(\d+)$/);
				return match ? Number(match[1]) : undefined;
			})
			.filter((id): id is number => typeof id === 'number' && Number.isFinite(id)),
	);

	return Object.entries(status)
		.filter(([key]) => key.startsWith('zwavedevice:'))
		.map(([key, value]) => {
			const componentIdFromKey = Number(key.split(':')[1]);
			const componentId = Number(value?.id ?? componentIdFromKey);
			const nodeId = Number(value?.node_id);
			if (!Number.isFinite(componentId) || !Number.isFinite(nodeId)) return undefined;

			return {
				component_id: componentId,
				node_id: nodeId,
				connected: Boolean(value?.connected),
				fw_ver: value?.fw_ver,
				has_vcomp: vcompIds.has(componentId),
			} satisfies GatewayZWaveNode;
		})
		.filter((node): node is GatewayZWaveNode => Boolean(node));
};

/**
 * Cloud Z-Wave end devices paired to this gateway.
 * Prefer `status.device._gw` matching the gateway id; fall back to mesh node ids on the gateway.
 */
export const getPairedZWaveCloudDevices = (
	gatewayDevice: Device,
	gatewayStatus: Record<string, any> | undefined,
	devicesMap: Record<string, Device>,
): Device[] => {
	const gatewayId = real_id(gatewayDevice?.id);
	const knownNodeIds = new Set(getGatewayZWaveNodes(gatewayStatus).map((node) => node.node_id));

	return Object.values(devicesMap)
		.filter((device) => {
			if (!is_gen_z(device)) return false;

			const endStatus = devices_status.getByRealId(device.id) as Record<string, any> | undefined;
			const gatewayMac = endStatus?.device?._gw;
			if (gatewayMac && gatewayId.includes(String(gatewayMac))) {
				return true;
			}

			const nodeId = parseZWaveNodeIdFromDeviceId(device.id);
			return nodeId !== undefined && knownNodeIds.has(nodeId);
		})
		.sort((left, right) => {
			const leftNode = parseZWaveNodeIdFromDeviceId(left.id) ?? Number.MAX_SAFE_INTEGER;
			const rightNode = parseZWaveNodeIdFromDeviceId(right.id) ?? Number.MAX_SAFE_INTEGER;
			if (leftNode !== rightNode) return leftNode - rightNode;
			return getZWaveChannelIndex(left) - getZWaveChannelIndex(right);
		});
};

/**
 * Cloud Z-Wave base devices in the gateway room whose node id is not present on the gateway.
 */
export const getOrphanZWaveCloudDevices = (
	gatewayDevice: Device,
	gatewayStatus: Record<string, any> | undefined,
	devicesMap: Record<string, Device>,
): Device[] => {
	const gatewayNodes = getGatewayZWaveNodes(gatewayStatus);
	const knownNodeIds = new Set(gatewayNodes.map((node) => node.node_id));
	const gatewayRoomId = gatewayDevice?.room_id;

	const orphansByBaseId: Record<string, Device> = {};

	Object.values(devicesMap).forEach((device) => {
		if (!is_gen_z(device)) return;
		if (gatewayRoomId != null && device.room_id !== gatewayRoomId) return;

		const baseId = real_id(device.id);
		const nodeId = parseZWaveNodeIdFromDeviceId(device.id);
		if (nodeId === undefined) return;
		if (knownNodeIds.has(nodeId)) return;

		const isBaseDevice = device.id === baseId;
		if (!orphansByBaseId[baseId] || isBaseDevice) {
			orphansByBaseId[baseId] = isBaseDevice ? device : { ...device, id: baseId };
		}
	});

	return Object.values(orphansByBaseId);
};

/**
 * All cloud Z-Wave base devices in the gateway room (used after HardReset cleanup).
 */
export const getRoomZWaveCloudDevices = (
	gatewayDevice: Device,
	devicesMap: Record<string, Device>,
): Device[] => {
	const gatewayRoomId = gatewayDevice?.room_id;
	const devicesByBaseId: Record<string, Device> = {};

	Object.values(devicesMap).forEach((device) => {
		if (!is_gen_z(device)) return;
		if (gatewayRoomId != null && device.room_id !== gatewayRoomId) return;

		const baseId = real_id(device.id);
		const isBaseDevice = device.id === baseId;
		if (!devicesByBaseId[baseId] || isBaseDevice) {
			devicesByBaseId[baseId] = isBaseDevice ? device : { ...device, id: baseId };
		}
	});

	return Object.values(devicesByBaseId);
};

export const getIncompleteGatewayZWaveNodes = (
	gatewayStatus: Record<string, any> | undefined,
): GatewayZWaveNode[] => {
	return getGatewayZWaveNodes(gatewayStatus).filter((node) => !node.has_vcomp);
};

/**
 * Resolves gateway component id for Wave RPCs (`ZWaveDevice.*`).
 * Prefer per-channel gateway vcomps (`switch:200`) then `_gwc` (`zwavedevice:202`).
 * Never use mesh node ids (`XZ…N10` → 10) or end-device control ids (`light:100`).
 */
export const resolveZWaveGatewayComponentId = (
	device: Device,
	status?: Record<string, any> | null,
	settings?: Record<string, any> | null,
): number | undefined => {
	return resolveZWaveGatewayComponentRef(device, status, settings)?.id;
};

/** True for gateway virtual / ZWaveDevice component ids (200+), not mesh nodes or light:100. */
const isGatewayComponentId = (id: number): boolean => id >= 200;

const getControlTypePrefix = (device: Device): string => {
	if (device?.type === 'ZW-COVER' || device?.category === 'roller') return 'cover:';
	if (device?.type === 'ZW-DIMMER' || device?.category === 'light') return 'light:';
	if (device?.type === 'ZW-INPUT' || device?.category === 'inputs_reader') return 'input:';
	return 'switch:';
};

/**
 * Gateway `ZWaveDevice` component id from end-device status (`_gwc: "zwavedevice:202"`).
 * Used by gateway RPCs when no per-channel vcomp exists.
 */
export const resolveZWaveDeviceComponentId = (
	device: Device,
	status?: Record<string, any> | null,
	settings?: Record<string, any> | null,
): number | undefined => {
	const gwc = status?.device?._gwc;
	if (typeof gwc === 'string' && gwc.startsWith('zwavedevice:')) {
		const id = Number(gwc.split(':')[1]);
		if (Number.isFinite(id) && isGatewayComponentId(id)) {
			return id;
		}
	}

	const settingsId = Number(settings?.device?.id);
	if (Number.isFinite(settingsId) && isGatewayComponentId(settingsId)) {
		return settingsId;
	}

	// Offline / missing end-device status: map mesh node → gateway zwavedevice:* via gateway status.
	const nodeId = parseZWaveNodeIdFromDeviceId(device?.id);
	if (nodeId === undefined) return undefined;

	const gatewayId = status?.device?._gw;
	const devicesMap = get(devices);
	const gatewayDevice = Object.values(devicesMap).find((candidate) => {
		if (is_gen_z(candidate)) return false;
		const candidateStatus = devices_status.getByRealId(candidate.id) as
			| Record<string, any>
			| undefined;
		if (!candidateStatus) return false;
		if (gatewayId && real_id(candidate.id).includes(String(gatewayId))) {
			return getGatewayZWaveNodes(candidateStatus).some((node) => node.node_id === nodeId);
		}
		return getGatewayZWaveNodes(candidateStatus).some((node) => node.node_id === nodeId);
	});

	if (!gatewayDevice) return undefined;
	const gatewayStatus = devices_status.getByRealId(gatewayDevice.id) as
		| Record<string, any>
		| undefined;
	const match = getGatewayZWaveNodes(gatewayStatus).find((node) => node.node_id === nodeId);
	return match?.component_id;
};

/**
 * Component settings key for this Wave card (`switch:100`, `cover:100`, …).
 */
export const getZWaveComponentSettingsKey = (device: Device): string => {
	return get_z_wave_channel_structure_from_category(device);
};

/**
 * Custom name from the control component only (`switch:100.name`).
 */
export const getZWaveChannelConfigName = (
	device: Device,
	_status?: Record<string, any> | null,
	settings?: Record<string, any> | null,
): string => {
	if (!is_gen_z(device)) return '';

	const settingsPayload = settings || get(devices_settings)?.[real_id(device.id)] || {};
	const componentKey = getZWaveComponentSettingsKey(device);
	const componentName = settingsPayload?.[componentKey]?.name;
	if (typeof componentName === 'string' && componentName.trim()) {
		return componentName.trim();
	}
	return '';
};

/**
 * Custom name on the Z-Wave node (`settings.device.name` / gateway `zwavedevice:N.name`).
 */
export const getZWaveNodeDeviceConfigName = (
	device: Device,
	status?: Record<string, any> | null,
	settings?: Record<string, any> | null,
): string => {
	if (!is_gen_z(device)) return '';

	const settingsPayload = settings || get(devices_settings)?.[real_id(device.id)] || {};
	const fromDevice =
		typeof settingsPayload?.device?.name === 'string' ? settingsPayload.device.name.trim() : '';
	if (fromDevice) return fromDevice;

	const componentId = resolveZWaveDeviceComponentId(device, status, settingsPayload);
	if (typeof componentId !== 'number') return '';

	const gateway = findZWaveGatewayForDevice(device, status);
	if (!gateway) return '';

	const gatewaySettings = get(devices_settings)?.[real_id(gateway.id)];
	const fromGateway =
		typeof gatewaySettings?.[`zwavedevice:${componentId}`]?.name === 'string'
			? gatewaySettings[`zwavedevice:${componentId}`].name.trim()
			: '';
	return fromGateway || '';
};

/**
 * @deprecated Prefer {@link getZWaveChannelConfigName} or {@link getZWaveNodeDeviceConfigName}.
 * Channel name, else node device name.
 */
export const getZWaveDeviceConfigName = (
	device: Device,
	status?: Record<string, any> | null,
	settings?: Record<string, any> | null,
): string => {
	return (
		getZWaveChannelConfigName(device, status, settings) ||
		getZWaveNodeDeviceConfigName(device, status, settings)
	);
};

/** Wave product type (`ZW-COVER`), from `type` or the raw BE `code`. */
export const getZWaveProductType = (device: Device): string | undefined => {
	if (typeof device?.type === 'string' && device.type.startsWith('ZW-')) return device.type;
	const code = (device as Device & { code?: string })?.code;
	if (typeof code === 'string' && code.startsWith('ZW-')) return code;
	return undefined;
};

const parseTypedGatewayComponents = (components: unknown, typePrefix: string): string[] => {
	if (!Array.isArray(components)) return [];
	return components.filter(
		(key): key is string => typeof key === 'string' && key.startsWith(typePrefix),
	);
};

const pickGatewayOutputId = (typedMatches: string[], channelIndex: number): number | undefined => {
	if (typedMatches.length === 0) return undefined;
	const keyed = typedMatches[channelIndex] ?? typedMatches[0];
	if (typeof keyed !== 'string') return undefined;
	const id = Number(keyed.split(':')[1]);
	return Number.isFinite(id) && isGatewayComponentId(id) ? id : undefined;
};

/**
 * Gateway `zwavedevice:N.components` for this node — used when end-device status
 * omits `device.components` (common for single-output switches).
 */
const getGatewayNodeOutputComponents = (
	device: Device,
	status?: Record<string, any> | null,
	settings?: Record<string, any> | null,
): string[] => {
	const nodeComponentId = resolveZWaveDeviceComponentId(device, status, settings);
	if (typeof nodeComponentId !== 'number') return [];

	const gateway = findZWaveGatewayForDevice(device, status);
	if (!gateway) return [];

	const gatewayStatus = devices_status.getByRealId(gateway.id) as Record<string, any> | undefined;
	const nodeEntry = gatewayStatus?.[`zwavedevice:${nodeComponentId}`];
	return Array.isArray(nodeEntry?.components) ? nodeEntry.components : [];
};

/**
 * Id used in generated Wave titles (`Z-Wave Switch (201)`).
 * Prefer the per-channel gateway output from `device.components` when present
 * (matches gateway "Output (200)/(201)/(202)"), including single-output nodes.
 * If the end device omits components, read them from the gateway
 * `zwavedevice:N.components`. Only then fall back to `_gwc`.
 */
export const resolveZWaveNameComponentId = (
	device: Device,
	status?: Record<string, any> | null,
	settings?: Record<string, any> | null,
): number | undefined => {
	const typePrefix = getControlTypePrefix(device);
	const channelIndex = getZWaveChannelIndex(device);

	const fromEndDevice = pickGatewayOutputId(
		parseTypedGatewayComponents(status?.device?.components, typePrefix),
		channelIndex,
	);
	if (typeof fromEndDevice === 'number') return fromEndDevice;

	const fromGatewayNode = pickGatewayOutputId(
		parseTypedGatewayComponents(
			getGatewayNodeOutputComponents(device, status, settings),
			typePrefix,
		),
		channelIndex,
	);
	if (typeof fromGatewayNode === 'number') return fromGatewayNode;

	return resolveZWaveDeviceComponentId(device, status, settings);
};

/**
 * Generated label for Wave cards with no custom name: `Z-Wave Cover (203)`.
 * See {@link resolveZWaveNameComponentId} for which gateway id is used.
 */
export const getZWaveGeneratedDeviceName = (
	device: Device,
	status?: Record<string, any> | null,
	settings?: Record<string, any> | null,
): string => {
	const productType = getZWaveProductType(device);
	const friendlyName = productType ? devices_config_wave[productType]?.friendly_name : undefined;
	if (!friendlyName) return '';

	const componentId = resolveZWaveNameComponentId(device, status, settings);
	if (typeof componentId !== 'number') return friendlyName;

	return `${friendlyName} (${componentId})`;
};

/**
 * Wave UI title: channel name → node device name → generated label → cloud `device.name`.
 */
export const getZWaveDeviceDisplayName = (
	device: Device,
	status?: Record<string, any> | null,
	settings?: Record<string, any> | null,
): string => {
	if (!is_gen_z(device)) return device?.name || '';

	const customName = getZWaveDeviceConfigName(device, status, settings);
	if (customName) return customName;

	return getZWaveGeneratedDeviceName(device, status, settings) || device?.name || '';
};

/** Normalize editable Wave name: empty string → `null` for SetConfig. */
export const toZWaveComponentNameValue = (name: string | null | undefined): string | null => {
	if (name === null || name === undefined) return null;
	const trimmed = name.trim();
	return trimmed === '' ? null : trimmed;
};

/**
 * Set name on the Wave node via gateway `ZWaveDevice.SetConfig` (not channel components).
 * Pass `null` / empty to clear. Also patches local end-device + gateway settings.
 */
export const zwaveSetDeviceName = async (device: Device, name: string | null) => {
	if (!is_gen_z(device)) {
		return { isok: false, error: 'Invalid device' } as Request;
	}

	const nextName = toZWaveComponentNameValue(name);
	const status = devices_status.getByRealId(device.id) as Record<string, any> | undefined;
	const settings = get(devices_settings)?.[real_id(device.id)];
	const componentId = resolveZWaveDeviceComponentId(device, status, settings);
	const gateway = findZWaveGatewayForDevice(device, status);
	if (typeof componentId !== 'number' || !gateway) {
		return { isok: false, error: 'Missing Z-Wave gateway component id' } as Request;
	}

	const response = await gen2Request(gateway, 'ZWaveDevice.SetConfig', {
		id: componentId,
		config: { name: nextName, meta: {} },
	});

	if (response?.isok) {
		const endDeviceId = real_id(device.id);
		const endDeviceSettings = get(devices_settings)?.[endDeviceId] || settings || {};
		devices_settings.update_devices_settings(endDeviceId, {
			...endDeviceSettings,
			device: {
				...(endDeviceSettings.device || {}),
				name: nextName,
			},
		});

		const gatewayId = real_id(gateway.id);
		const gatewaySettings = get(devices_settings)?.[gatewayId] || {};
		const zwaveKey = `zwavedevice:${componentId}`;
		devices_settings.update_devices_settings(gatewayId, {
			...gatewaySettings,
			[zwaveKey]: {
				...(gatewaySettings[zwaveKey] || { id: componentId }),
				name: nextName,
			},
		});
	}

	return response;
};

/**
 * Set name on the Wave control component (Switch|Light|Cover|Input.SetConfig via XZ).
 * Pass `null` / empty to clear the component name.
 * Sensors without a control component fall back to gateway `ZWaveDevice.SetConfig`.
 */
export const zwaveSetComponentName = async (device: Device, name: string | null) => {
	if (!is_gen_z(device)) {
		return { isok: false, error: 'Invalid device' } as Request;
	}

	const nextName = toZWaveComponentNameValue(name);
	const componentPrefix = get_z_wave_component_from_category(device);

	if (componentPrefix === 'cover:') {
		return zwaveCoverSetConfig(device, { name: nextName });
	}

	if (componentPrefix === 'light:' || componentPrefix === 'switch:') {
		return zwaveActuatorSetConfig(device, { name: nextName });
	}

	if (componentPrefix === 'input:') {
		const componentId = getXzComponentId(device);
		const response = await xzRequest(device, 'Input.SetConfig', {
			id: componentId,
			config: { name: nextName },
		});
		if (response?.isok) {
			const endDeviceId = real_id(device.id);
			const existing = get(devices_settings)?.[endDeviceId] || {};
			const key = `input:${componentId}`;
			devices_settings.update_devices_settings(endDeviceId, {
				...existing,
				[key]: {
					...(existing[key] || {}),
					id: componentId,
					name: nextName,
				},
			});
		}
		return response;
	}

	return zwaveSetDeviceName(device, nextName);
};

/**
 * Cloud-rename this Wave interface card only (per-channel component names).
 * Skips when name is cleared (`null` / empty) so the list keeps the previous cloud name.
 */
export const syncZWaveCloudDeviceName = async (
	device: Device,
	name: string | null,
): Promise<boolean> => {
	const nextName = toZWaveComponentNameValue(name);
	if (!device?.id || !nextName) return true;

	const current = get(devices)[device.id] || device;
	const updated = { ...current, name: nextName };
	devices.update(updated);
	const response = await postReq('interface/device/save', {
		id: device.id,
		data: JSON.stringify(updated),
	});
	return Boolean(response?.isok);
};

const resolveZWaveGatewayComponentRef = (
	device: Device,
	status?: Record<string, any> | null,
	settings?: Record<string, any> | null,
): ResolvedGatewayComponentId | undefined => {
	const channelIndex = getZWaveChannelIndex(device);
	const typePrefix = getControlTypePrefix(device);
	const components = status?.device?.components;

	// Per-channel gateway outputs: components: ["switch:200", "switch:201"]
	if (Array.isArray(components)) {
		const typedMatches = components.filter(
			(key): key is string => typeof key === 'string' && key.startsWith(typePrefix),
		);
		const keyed = typedMatches[channelIndex] ?? components[channelIndex];
		if (typeof keyed === 'string') {
			const id = Number(String(keyed).split(':')[1]);
			if (Number.isFinite(id) && isGatewayComponentId(id)) {
				return { id, fromChannelComponent: true };
			}
		}
	}

	const zwaveDeviceId = resolveZWaveDeviceComponentId(device, status, settings);
	if (typeof zwaveDeviceId === 'number') {
		return { id: zwaveDeviceId, fromChannelComponent: false };
	}

	const gwc = status?.device?._gwc;
	if (typeof gwc === 'string' && gwc.startsWith(typePrefix)) {
		const id = Number(gwc.split(':')[1]);
		if (Number.isFinite(id) && isGatewayComponentId(id)) {
			return { id, fromChannelComponent: false };
		}
	}

	return undefined;
};

/**
 * End device is unreachable for gateway-side recovery actions.
 * Prefer explicit `online: false` / missing status over stale `online: true`.
 */
export const isZWaveEndDeviceOffline = (endDeviceStatus?: Record<string, any> | null): boolean =>
	endDeviceStatus?.online !== true;

/**
 * Gateway-side Wave actions (Remove failed / Reinterview):
 * online gateway + offline end device → enabled; offline gateway → disabled.
 */
export const isZWaveGatewayRecoveryActionEnabled = (
	device: Device,
	endDeviceStatus?: Record<string, any> | null,
	gatewayStatus?: Record<string, any> | null,
): boolean => {
	if (!is_gen_z(device)) return false;
	if (!isZWaveEndDeviceOffline(endDeviceStatus)) return false;
	return Boolean(gatewayStatus?.online);
};

/**
 * `ZWaveDevice.RemoveFailed` is only valid when the end device is unreachable
 * and its Wave gateway is online (RPC is sent to the gateway).
 */
export const isZWaveRemoveFailedActionEnabled = (
	device: Device,
	endDeviceStatus?: Record<string, any> | null,
	gatewayStatus?: Record<string, any> | null,
): boolean => isZWaveGatewayRecoveryActionEnabled(device, endDeviceStatus, gatewayStatus);

/**
 * Resolves gateway status and evaluates whether Remove failed should be enabled.
 */
export const resolveZWaveRemoveFailedActionEnabled = (
	device: Device,
	endDeviceStatus?: Record<string, any> | null,
): boolean => {
	const statusPayload = endDeviceStatus ?? devices_status.getByRealId(device.id);
	const gateway = findZWaveGatewayForDevice(device, statusPayload);
	if (!gateway) return false;
	const gatewayStatus = devices_status.getByRealId(gateway.id) as Record<string, any> | undefined;
	return isZWaveRemoveFailedActionEnabled(device, statusPayload, gatewayStatus);
};

/**
 * Resolves gateway + end-device status for Reinterview / Remove failed UI enablement.
 */
export const resolveZWaveGatewayRecoveryActionEnabled = (
	device: Device,
	endDeviceStatus?: Record<string, any> | null,
): boolean => {
	const statusPayload = endDeviceStatus ?? devices_status.getByRealId(device.id);
	const gateway = findZWaveGatewayForDevice(device, statusPayload);
	if (!gateway) return false;
	const gatewayStatus = devices_status.getByRealId(gateway.id) as Record<string, any> | undefined;
	return isZWaveGatewayRecoveryActionEnabled(device, statusPayload, gatewayStatus);
};
